// Client side of the MRC short-link service (backend: controllers/ShortLinkController.js).
//
// A share URL (".../signin/<aes>/<aes>/...") is far too long to read out, type or fit in a QR
// code comfortably, so the backend issues an MRC-owned short link (https://<host>/share/<code>)
// for it. The short link is resolved by MRC itself - there is no third-party shortener.
//
// Rule that matters most: short links are always looked up BY THE LONG URL they were issued
// for, never by list position. The lists the share dialogs display are filtered views of
// SharedFile.json (staff and client links are shown separately), so an index into one list is
// not an index into another; keying by position can hand out the link of a different case.

// Keep in sync with MAX_BATCH in the backend ShortLinkController.
export const SHORT_LINK_BATCH_SIZE = 25;

const CODE_PATTERN = /^[A-Za-z0-9_-]{5,40}$/;

export const isShortCode = (value) =>
  typeof value === "string" && CODE_PATTERN.test(value);

export const isShortUrl = (value) => {
  if (typeof value !== "string" || value.length > 200) return false;
  if (/[^\x21-\x7e]/.test(value)) return false; // printable ASCII only: no whitespace/control chars
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch (error) {
    return false;
  }
};

/**
 * The short link recorded on a SharedFile.json entry (or returned by the service), as
 * { shortCode, shortUrl } - or null when it is missing or does not look right.
 * Accepts anything: GetSharedList can return plain strings (e.g. "SharedFile.json not found").
 */
export function pickShortLink(item) {
  if (!item || typeof item !== "object") return null;
  const { shortCode, shortUrl } = item;
  if (!isShortCode(shortCode) || !isShortUrl(shortUrl)) return null;
  // The URL must actually end in its own code; otherwise the pair is inconsistent.
  if (!shortUrl.replace(/\/+$/, "").endsWith(`/${shortCode}`)) return null;
  return { shortCode, shortUrl };
}

// ---------------------------------------------------------------------------------------------
// Why a short link could not be had
//
// Sharing must never depend on this service, so every failure ends in "use the full link". But a
// UI that falls back SILENTLY is indistinguishable from one that never tried - which is exactly
// how a missing deployment, an expired session or a storage outage went unnoticed. So the
// reason is kept and shown next to the link ({ kind, status?, detail? }):
//
//   no-session   nobody is signed in (no token), so nothing was asked
//   session      HTTP 401: the session expired, or its token is not valid for this server
//   missing      HTTP 404/405 or a non-JSON answer: this server has no short-link service
//   busy         HTTP 429: too many requests from this network
//   unavailable  HTTP 5xx: the service or its storage is down
//   unreachable  no answer at all (network, CORS, timeout)
//   rejected     the server refused this particular address (detail = its reason code)
//   expired      the share is already over
//   unknown      anything else
// ---------------------------------------------------------------------------------------------

/** Turns a failed request into a failure reason. */
export function classifyFailure(error) {
  const response = error && error.response;
  const status = response && response.status;
  if (!status) return { kind: "unreachable" };
  if (status === 401) {
    const reason = response.data && typeof response.data.reason === "string" ? response.data.reason : undefined;
    return { kind: "session", status, detail: reason };
  }
  if (status === 404 || status === 405) return { kind: "missing", status };
  if (status === 429) return { kind: "busy", status };
  if (status >= 500) return { kind: "unavailable", status };
  return { kind: "unknown", status };
}

/** The reason for one item the server answered but did not issue a short link for. */
function classifyItem(result) {
  if (result.status === "expired") return { kind: "expired" };
  if (result.status === "rejected") {
    return { kind: "rejected", detail: typeof result.reason === "string" ? result.reason : undefined };
  }
  return { kind: "unknown", detail: typeof result.status === "string" ? result.status : undefined };
}

// A developer running the UI on localhost against the live HOST is refused ON PURPOSE: the
// allow-list only knows the live site, and a short link that opens a localhost page is useless to
// anyone else. Say what to do about it instead of leaving them to decode a reason code.
const currentHostname = () =>
  (typeof window !== "undefined" && window.location && window.location.hostname) || "";

const isLocalhost = (hostname) => /^(localhost|127\.0\.0\.1|\[::1\])$/.test(hostname);

const LOCAL_HINTS = {
  // the address is plain http://localhost...: only accepted when local testing is switched on
  protocol:
    " This page runs on localhost: to try short links locally, set SHORTLINK_ALLOW_LOCALHOST=true in the backend's .env and restart it.",
  // the address is on a site the backend does not know (e.g. a live link while HOST is local)
  host:
    " This page runs on localhost: add that site to SHORTLINK_ALLOWED_HOSTS (or set SHORTLINK_ALLOW_LOCALHOST=true for a localhost address) in the backend's .env and restart it.",
};

const localhostHint = (failure, hostname) =>
  isLocalhost(hostname) && Object.prototype.hasOwnProperty.call(LOCAL_HINTS, failure.detail)
    ? LOCAL_HINTS[failure.detail]
    : "";

/**
 * One plain-language sentence for a failure reason (the UI adds "showing the full link").
 * `hostname` is the host the UI is served from (defaults to the page's own); tests inject it.
 */
export function describeFailure(failure, { hostname = currentHostname() } = {}) {
  const f = failure || {};
  switch (f.kind) {
    case "no-session":
      return "You are not signed in, so no short link could be created.";
    case "session":
      return "Your session has expired or is not valid for this server, so no short link could be created. Please sign in again.";
    case "missing":
      return "This server has no short-link service (is the backend up to date?).";
    case "busy":
      return "Too many short-link requests from this network. Please wait a minute.";
    case "unavailable":
      return "The short-link service is temporarily unavailable.";
    case "unreachable":
      return "Could not reach the short-link service.";
    case "rejected":
      return `The server did not accept this address for a short link${f.detail ? ` (reason: ${f.detail})` : ""}.${localhostHint(f, hostname)}`;
    case "expired":
      return "This share has expired, so its link no longer works. Choose a new expiry date to get a new short link.";
    default:
      return `A short link could not be created${f.status ? ` (HTTP ${f.status})` : ""}.`;
  }
}

/** Whether asking again can help (a refused address or an over share will not change). */
export const isRetryable = (failure) => !failure || (failure.kind !== "rejected" && failure.kind !== "expired");

/**
 * Asks the backend for short links for a list of share URLs (in batches).
 *
 * Resolves to a Map<longUrl, { shortCode, shortUrl }> that contains only the URLs which got an
 * ACTIVE short link. Anything else - rejected, expired, not signed in, service down - is simply
 * absent, and callers keep using the long link, so sharing never breaks. Never throws.
 *
 * Pass a Map as `failures` to learn WHY a URL is absent: it receives url -> { kind, ... }.
 *
 * @param {object}   args
 * @param {object}   args.http       axios-like client ({ post })
 * @param {string}   args.baseUrl    backend base URL (UploadApi)
 * @param {string}   args.token      the signed-in user's JWT
 * @param {Array<{url:string, expiryDate?:string}>} args.items
 * @param {string}   [args.folderName] label recorded in the audit log
 * @param {Map}      [args.failures]   filled with the reason for every URL that got no short link
 */
export async function requestShortLinks({ http, baseUrl, token, items, folderName = "", failures }) {
  const found = new Map();
  const fail = (urls, failure) => {
    if (failures) urls.forEach((url) => failures.set(url, failure));
  };

  const unique = [];
  const seen = new Set();
  for (const item of items || []) {
    const url = item && item.url;
    if (typeof url !== "string" || !url || seen.has(url)) continue;
    seen.add(url);
    unique.push({ url, expiryDate: (item && item.expiryDate) || "" });
  }

  if (!http || !token) {
    fail(
      unique.map((item) => item.url),
      { kind: "no-session" }
    );
    return found;
  }

  for (let start = 0; start < unique.length; start += SHORT_LINK_BATCH_SIZE) {
    const batch = unique.slice(start, start + SHORT_LINK_BATCH_SIZE);
    const urls = batch.map((item) => item.url);
    try {
      const response = await http.post(
        `${baseUrl}/apiUpload/short-links`,
        { items: batch, folderName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const results = response && response.data && Array.isArray(response.data.results) ? response.data.results : null;
      if (!results) {
        // 2xx but not our JSON (e.g. a proxy or SPA fallback answering for a route that is not there)
        fail(urls, { kind: "missing", status: response && response.status });
        continue;
      }
      for (const result of results) {
        if (!result || typeof result.url !== "string") continue;
        if (result.status === "active") {
          const link = pickShortLink({ shortCode: result.code, shortUrl: result.shortUrl });
          if (link) found.set(result.url, link);
        } else {
          fail([result.url], classifyItem(result));
        }
      }
      // anything the server did not answer for at all, or answered with an unusable link
      fail(
        urls.filter((url) => !found.has(url) && !(failures && failures.has(url))),
        { kind: "unknown" }
      );
    } catch (error) {
      // unavailable / unauthorised: the long link stays in use for this batch
      fail(urls, classifyFailure(error));
    }
  }
  return found;
}

/**
 * True when a share's expiry day (YYYY-MM-DD) is already over, so no short link is worth
 * requesting for it. Conservative on purpose: only days strictly before today (UTC) count.
 */
export function isExpiredDate(expiryDate, now = new Date()) {
  if (typeof expiryDate !== "string" || !/^\d{4}-\d{2}-\d{2}/.test(expiryDate)) return false;
  return expiryDate.slice(0, 10) < now.toISOString().slice(0, 10);
}

/** What to hand to the user for a row: its short link when known, otherwise the long URL. */
export const linkFor = (links, url) =>
  (url && links && links[url] && links[url].shortUrl) || url || "";
